/**
 * Account Deletion Info Page — the PUBLIC, signed-out explanation of how to delete a
 * Trade Loop account (Card 12, 2026-08-25).
 *
 * WHY THIS EXISTS SEPARATELY FROM DeleteAccountPage.
 *
 * Google Play's Data safety declaration takes a "Delete account URL" that is published on the
 * store listing, and its stated requirements are that the page name the app, show the steps, and
 * say what is deleted and what is kept. `/delete-account` cannot serve that: it sits behind
 * `ProtectedRoute`, so the exact person most likely to follow the link from a store listing — one
 * who has already uninstalled the app, or cannot get back into their account — is redirected to a
 * login screen and learns nothing. Card 12 assumed this was "a declaration step, not new code";
 * checking the route showed otherwise.
 *
 * Performing the deletion still requires signing in, which is correct and deliberate: only the
 * account holder may delete the account, and the API re-checks the password. This page explains
 * and routes; it never deletes.
 *
 * THE COPY HERE MUST STAY TRUE TO `DeleteAccountPage.tsx` AND THE API. Saying otherwise on a page
 * Google publishes would be a false data-handling claim — a worse problem than the one this page
 * was added to solve. Every claim below was checked against the endpoints, not against the UI copy:
 *
 *   - There are TWO outcomes, not one. `DELETE /api/auth/account` DEACTIVATES (sets `is_inactive`
 *     + `deactivated_at`, signs the session out, erases NOTHING, reversible by signing in).
 *     `DELETE /api/auth/account/permanent` is the irreversible one.
 *   - Permanent deletion is a TOMBSTONE, not a cascade (Bug 23): the profile row survives stripped
 *     of PII and renamed "Former Trade Loop User", so a partner's jointly-owned referrals, threads
 *     and revenue keep their attribution.
 *   - BOTH endpoints refuse with 400 `ACTIVE_SUBSCRIPTION` while a subscription is active
 *     (auth.routes.ts:2726 and :2838). A page that omits that sends people into a dead end.
 */

import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ChevronLeft, Trash2, PauseCircle } from 'lucide-react';

export default function AccountDeletionInfoPage() {
  return (
    <div className="min-h-screen bg-gray-50 px-4 py-8">
      <div className="max-w-3xl mx-auto">
        <div className="mb-6">
          <Link to="/">
            <Button variant="outline" className="flex items-center gap-2">
              <ChevronLeft className="w-4 h-4" />
              Back to Home
            </Button>
          </Link>
        </div>

        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Deleting your Trade Loop account
        </h1>
        {/* Names the app as it appears on the store listing, which Google asks for explicitly. */}
        <p className="text-gray-600 mb-8">
          This page explains how to delete your account and the data associated with it in
          Trade Loop (listed on Google Play as <strong>Trade Loop Network</strong>), published by
          Trade Loop Holdings, Inc.
        </p>

        {/* Both outcomes up front and as equals. Deactivation used to sit at the bottom of this
            page framed as an afterthought, which under-sells a reversible option to someone who
            may only want to step away. */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-3">Two options</h2>
            <p className="text-gray-700 mb-4">
              The same screen in the app offers a choice. Read both before you decide — one can be
              undone and one cannot.
            </p>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <PauseCircle className="w-5 h-5 text-blue-600 shrink-0" />
                  <span className="font-semibold text-gray-900">Deactivate</span>
                </div>
                <p className="text-sm text-gray-700">
                  Reversible. You are signed out and stop appearing to your partners, but{' '}
                  <span className="font-medium">nothing is erased</span>. Signing in again
                  reactivates the account exactly as you left it.
                </p>
              </div>
              <div className="border border-red-200 bg-red-50/40 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Trash2 className="w-5 h-5 text-red-600 shrink-0" />
                  <span className="font-semibold text-gray-900">Delete permanently</span>
                </div>
                <p className="text-sm text-gray-700">
                  <span className="font-medium">Cannot be undone.</span> Your personal data is
                  erased and your sign-in is removed, so the account cannot be recovered. See what
                  is deleted and what is kept below.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Verified against auth.routes.ts:2726 and :2838 — both endpoints 400 on an active
            subscription. Omitting it would send people into a dead end. */}
        <Card className="mb-6 border-amber-200 bg-amber-50/50">
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              Cancel an active subscription first
            </h2>
            <p className="text-gray-700">
              If you have an active paid subscription, both options are blocked until it is
              cancelled. The app will tell you if this applies, and offers to cancel the
              subscription and delete the account in a single step.
            </p>
          </CardContent>
        </Card>

        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex items-start gap-3 mb-4">
              <Trash2 className="w-5 h-5 text-red-600 mt-1 shrink-0" />
              <h2 className="text-xl font-semibold text-gray-900">
                How to do either one
              </h2>
            </div>
            <ol className="list-decimal pl-6 space-y-2 text-gray-700">
              <li>
                Sign in to Trade Loop, either in the app or at{' '}
                <span className="font-medium">gotradeloop.com</span>. You can sign in even if you
                have already uninstalled the app.
              </li>
              <li>Open <span className="font-medium">Settings</span>.</li>
              <li>
                Choose <span className="font-medium">Delete / Deactivate Account</span>.
              </li>
              <li>
                Choose either <span className="font-medium">Deactivate my account</span> or{' '}
                <span className="font-medium">Delete my account permanently</span>, confirm with
                your password, and follow the prompt.
              </li>
            </ol>
            <div className="mt-6">
              <Link to="/login?redirect=/delete-account">
                <Button className="bg-red-600 hover:bg-red-700 text-white">
                  Sign in to deactivate or delete my account
                </Button>
              </Link>
            </div>
            <p className="mt-4 text-sm text-gray-600">
              Cannot sign in? Email{' '}
              <span className="font-medium">support@gotradeloop.com</span> from the address on the
              account and we will action the deletion for you.
            </p>
          </CardContent>
        </Card>

        <Card className="mb-6">
          <CardContent className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-1">
              Permanent deletion: what is deleted, and what is kept
            </h2>
            {/* Scoped in the heading AND restated here: deactivation erases nothing, and a reader
                skimming to this section must not take it for the general case. */}
            <p className="text-sm text-gray-600 mb-4">
              This section describes <span className="font-medium">permanent deletion only</span>.
              Deactivation erases nothing.
            </p>
            <p className="font-medium text-gray-900 mb-2">Deleted permanently</p>
            <ul className="list-disc pl-6 space-y-1 text-gray-700 mb-5">
              <li>Your profile: name, email address, phone number, company details and bio.</li>
              <li>Your service areas, trades and other professional profile information.</li>
              <li>Your profile photo and company logo.</li>
              <li>Your billing history and any stored payment method details.</li>
              <li>
                Your sign-in identity, so the account cannot be signed back into. This is not
                reversible.
              </li>
            </ul>

            <p className="font-medium text-gray-900 mb-2">Kept, and why</p>
            <p className="text-gray-700 mb-2">
              A referral is shared between two contractors. Deleting your account does not delete
              your partners' records of work you sent or received, because that history belongs to
              them as much as it did to you. So the following survive, with your name replaced by
              a neutral <span className="font-medium">Former Trade Loop User</span> that cannot be
              messaged or opened:
            </p>
            <ul className="list-disc pl-6 space-y-1 text-gray-700">
              <li>Referrals you sent to, or received from, another member.</li>
              <li>The message threads on those referrals, and any photos or files attached.</li>
            </ul>
            <p className="text-gray-700 mt-4">
              Deletion takes effect immediately. Records we are required to keep for legal,
              accounting or fraud-prevention reasons are retained only for as long as that
              obligation lasts, and are not used for anything else.
            </p>
          </CardContent>
        </Card>

        <p className="text-sm text-gray-600">
          Questions about your data? See our{' '}
          <Link to="/legal" className="text-blue-600 hover:underline">
            Privacy Policy
          </Link>{' '}
          or contact <span className="font-medium">support@gotradeloop.com</span>.
        </p>
      </div>
    </div>
  );
}
